/**
 * Keys, and why they are fussy.
 *
 * A field key is stored inside every product that attaches it and inside every
 * answer document ever written against those products. It is therefore
 * permanent: there is no rename endpoint anywhere in this codebase, and adding
 * one later would mean rewriting historical applications. The cost of that is
 * paid here, by making the key hard to get wrong in the first place - derived
 * from the label, shown to the author before they commit, and checked.
 */
const SEGMENT = /^[a-z][a-z0-9_]*$/;
export const RESERVED_SEGMENTS = new Set(["constructor", "prototype", "__proto__"]);
/** `Year Built` -> `year_built`. Also the preview the field form shows live. */
export function toSnakeCase(text) {
    return text
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .replace(/_{2,}/g, "_");
}
export function deriveFieldKey(entityKey, label) {
    return `${entityKey}.${toSnakeCase(label)}`;
}
/** An entity key is one segment: `location`, `named_insured`. */
export function checkEntityKey(key) {
    if (!key)
        return { message: "An entity key is required." };
    if (!SEGMENT.test(key)) {
        return { message: `'${key}' must be lower case letters, digits and underscores, starting with a letter.` };
    }
    if (RESERVED_SEGMENTS.has(key))
        return { message: `'${key}' is a reserved word.` };
    if (key.length > 60)
        return { message: "An entity key is at most 60 characters." };
    return null;
}
/** A field key is exactly two segments: the entity, then the leaf. Deeper
 *  structures are modelled as their own entity rather than as a deeper key,
 *  which keeps the answer document addressable by a plain path. */
export function checkFieldKey(key) {
    if (!key)
        return { message: "A field key is required." };
    const parts = key.split(".");
    if (parts.length !== 2) {
        return { message: `'${key}' must be exactly entity.field, for example 'location.year_built'.` };
    }
    for (const part of parts) {
        const problem = checkEntityKey(part);
        if (problem)
            return problem;
    }
    if (key.length > 128)
        return { message: "A field key is at most 128 characters." };
    return null;
}
/** Domain and section keys follow the same one-segment rule as entities. */
export const checkSimpleKey = checkEntityKey;
//# sourceMappingURL=keys.js.map